import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { helper } from 'src/environments/helper';
import { GeneralService } from './general.service';
import { NotificationService } from 'src/app/services/notification.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-general',
    templateUrl: './general.component.html',
    styleUrls: ['./general.component.scss']
})
export class GeneralComponent implements OnInit {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    helper = helper;
    isLoading = false;

    generalForm!: FormGroup;

    generalModified = false;

    private initialGeneralValues: any;
    private originalData: any[] = [];

    constructor(
        private fb: FormBuilder,
        private generalService: GeneralService,
        private notificationService: NotificationService,
        public permissionsService: PermissionsService
    ) { }

    ngOnInit(): void {
        this.initializeForms();
        this.loadData();

        if (!this.permissionsService.hasPermission('dgaspc-module.settings.general-settings.update')) {
            this.generalForm.disable();
        }
    }

    private initializeForms(): void {
        this.generalForm = this.fb.group({
            absente_programari: ['']
        });

        this.generalForm.valueChanges.subscribe(() => {
            this.generalModified = this.hasFormChanged(this.generalForm, this.initialGeneralValues);
        });
    }

    private hasFormChanged(form: FormGroup, initialValues: any): boolean {
        if (!initialValues) return false;
        return JSON.stringify(form.value) !== JSON.stringify(initialValues);
    }

    private loadData(): void {
        this.isLoading = true;
        this.generalService.list().subscribe({
            next: (response: any) => {
                if (response.errors === false && response.data) {
                    this.populateForms(response.data);
                    this.originalData = response.data;
                }
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error loading data:', error);
                this.isLoading = false;
            }
        });
    }

    private populateForms(data: any[]): void {
        const dataMap = this.createDataMap(data);

        const generalValues = {
            absente_programari: dataMap['absente_programari'] !== undefined
                ? parseInt(dataMap['absente_programari'])
                : ''
        };

        this.generalForm.patchValue(generalValues);
        this.initialGeneralValues = { ...generalValues };
    }

    private createDataMap(data: any[]): { [key: string]: string } {
        const map: { [key: string]: string } = {};
        data.forEach(item => {
            map[item.name] = item.value;
        });
        return map;
    }

    updateGeneral(): void {
        if (this.generalForm.valid) {
            const changedFields = this.getChangedFields(this.generalForm.value, this.initialGeneralValues);
            const updates = this.createUpdatePayloads(changedFields);
            this.updateMultipleSettings(updates);
        }
    }

    private getChangedFields(currentValues: any, initialValues: any): { [key: string]: any } {
        const changed: { [key: string]: any } = {};
        Object.keys(currentValues).forEach(key => {
            if (currentValues[key] !== initialValues[key]) {
                changed[key] = currentValues[key];
            }
        });
        return changed;
    }

    private createUpdatePayloads(changedFields: { [key: string]: any }): any[] {
        const payloads: any[] = [];

        Object.keys(changedFields).forEach(fieldName => {
            const originalItem = this.originalData.find(item => item.name === fieldName);
            if (originalItem) {
                payloads.push({
                    id: originalItem.id,
                    name: originalItem.name,
                    value: changedFields[fieldName]
                });
            }
        });

        return payloads;
    }

    private updateMultipleSettings(payloads: any[]): void {
        if (payloads.length === 0) {
            return;
        }

        this.isLoading = true;
        let completedRequests = 0;
        let hasError = false;

        payloads.forEach(payload => {
            this.generalService.update(payload).subscribe({
                next: (response: any) => {
                    completedRequests++;

                    if (response.errors !== false) {
                        hasError = true;
                    }

                    if (completedRequests === payloads.length) {
                        if (!hasError) {
                            this.loadData();
                            this.generalModified = false;
                            this.initialGeneralValues = { ...this.generalForm.value };
                        }
                        this.isLoading = false;
                    }
                },
                error: (err) => {
                    let errorMessage;

                    if (!hasError) {
                        try {
                            errorMessage = err.error.errors.message[0];
                        } catch (error) {
                            errorMessage = helper.dialogConfig.generalMessages.error;
                        }
                    }

                    completedRequests++;
                    hasError = true;

                    if (completedRequests === payloads.length) {
                        this.isLoading = false;
                        this.notificationService.warningSwal(
                            this.errorTitle,
                            errorMessage,
                            this.errorIcon
                        );
                    }
                }
            });
        });
    }

    clearCache() {
        Swal.fire({
            title: helper.swalTextConfig.commonTitles.areYouSure,
            text: helper.swalTextConfig.generalMessages.cleanCache,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: helper.swalTextConfig.buttonLabels.confirm,
            cancelButtonText: helper.swalTextConfig.buttonLabels.close,
            showLoaderOnConfirm: true,
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                this.generalService.clearCache().subscribe(() => {
                    Swal.fire({
                        title: helper.swalTextConfig.headers.deleted,
                        text: helper.swalTextConfig.generalMessages.cleanCacheSuccess,
                        icon: 'success',
                        confirmButtonText: helper.swalTextConfig.buttonLabels.ok,
                    });
                });
            }
        });
    }
}